import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckSquare, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

// Strip everything except digits (and a leading +) so 514-555-1234 and 5145551234 are equal
const normalizePhone = (p) => (p || "").replace(/[^\d+]/g, "");

export default function PublicSignup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [error, setError] = useState("");
  const [f, setF] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    phone_confirm: "",
    email: "",
    email_confirm: "",
    password: "",
    password_confirm: "",
    role_referee: false,
    role_scorer: false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/signup-info/${token}`);
        setBusinessName(data.business_name);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Lien invalide");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.last_name || !f.email || !f.password) {
      setError("Prénom, nom, courriel et mot de passe requis");
      return;
    }
    if (f.email.trim().toLowerCase() !== f.email_confirm.trim().toLowerCase()) {
      setError("Les deux courriels ne correspondent pas");
      return;
    }
    if (f.phone && normalizePhone(f.phone) !== normalizePhone(f.phone_confirm)) {
      setError("Les deux numéros de téléphone ne correspondent pas");
      return;
    }
    if (f.password.length < 8) {
      setError("Mot de passe min. 8 caractères");
      return;
    }
    if (f.password !== f.password_confirm) {
      setError("Les deux mots de passe ne correspondent pas");
      return;
    }
    if (!f.role_referee && !f.role_scorer) {
      setError("Choisis au moins un rôle : arbitre, marqueur ou les deux");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const roles = [];
      if (f.role_referee) roles.push("referee");
      if (f.role_scorer) roles.push("scorer");
      // Send only the fields the backend expects
      await api.post(`/public/signup/${token}`, {
        first_name: f.first_name,
        last_name: f.last_name,
        phone: f.phone,
        email: f.email,
        password: f.password,
        roles,
      });
      await refresh();
      navigate("/officiel", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  // Live confirmation status
  const emailMatch =
    f.email && f.email_confirm && f.email.trim().toLowerCase() === f.email_confirm.trim().toLowerCase();
  const phoneMatch = f.phone && f.phone_confirm && normalizePhone(f.phone) === normalizePhone(f.phone_confirm);
  const pwdMatch = f.password && f.password_confirm && f.password === f.password_confirm;

  const mismatchClass = (val, confirm, ok) => {
    if (!confirm) return "input-box";
    return ok ? "input-box border-black" : "input-box border-red-600";
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4 md:p-6" data-testid="public-signup-page">
      <div className="w-full max-w-lg">
        <div className="mb-8">
          <Logo size={40} />
        </div>
        <div className="card-flat">
          <div className="label mb-1">Inscription officiel</div>
          <h2 className="font-serif text-4xl mb-2">{businessName || "Inscription"}</h2>
          <p className="text-sm text-[var(--muted)] mb-6 leading-relaxed">
            Crée ton compte pour recevoir les demandes de disponibilités par SMS et voir tes parties assignées.
          </p>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label block mb-1.5">Prénom *</label>
                <input
                  value={f.first_name}
                  onChange={(e) => setF({ ...f, first_name: e.target.value })}
                  className="input-box"
                  autoComplete="given-name"
                  data-testid="signup-firstname-input"
                />
              </div>
              <div>
                <label className="label block mb-1.5">Nom *</label>
                <input
                  value={f.last_name}
                  onChange={(e) => setF({ ...f, last_name: e.target.value })}
                  className="input-box"
                  autoComplete="family-name"
                  data-testid="signup-lastname-input"
                />
              </div>
            </div>

            {/* Cellulaire + confirmation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label block mb-1.5">Cellulaire</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={f.phone}
                  onChange={(e) => setF({ ...f, phone: e.target.value })}
                  className="input-box"
                  placeholder="514-555-1234"
                  autoComplete="tel"
                  data-testid="signup-phone-input"
                />
              </div>
              <div>
                <label className="label block mb-1.5">Confirmer cellulaire</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={f.phone_confirm}
                  onChange={(e) => setF({ ...f, phone_confirm: e.target.value })}
                  className={mismatchClass(f.phone, f.phone_confirm, phoneMatch)}
                  placeholder="514-555-1234"
                  autoComplete="off"
                  onPaste={(e) => e.preventDefault()}
                  data-testid="signup-phone-confirm-input"
                />
                {f.phone_confirm && !phoneMatch && (
                  <p className="font-mono text-[10px] text-red-600 mt-1" data-testid="phone-mismatch">
                    Les deux numéros ne correspondent pas
                  </p>
                )}
              </div>
            </div>

            {/* Courriel + confirmation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label block mb-1.5">Courriel *</label>
                <input
                  type="email"
                  inputMode="email"
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                  className="input-box"
                  autoComplete="email"
                  data-testid="signup-email-input"
                />
              </div>
              <div>
                <label className="label block mb-1.5">Confirmer courriel *</label>
                <input
                  type="email"
                  inputMode="email"
                  value={f.email_confirm}
                  onChange={(e) => setF({ ...f, email_confirm: e.target.value })}
                  className={mismatchClass(f.email, f.email_confirm, emailMatch)}
                  autoComplete="off"
                  onPaste={(e) => e.preventDefault()}
                  data-testid="signup-email-confirm-input"
                />
                {f.email_confirm && !emailMatch && (
                  <p className="font-mono text-[10px] text-red-600 mt-1" data-testid="email-mismatch">
                    Les deux courriels ne correspondent pas
                  </p>
                )}
              </div>
            </div>

            {/* Mot de passe + confirmation */}
            <div>
              <label className="label block mb-1.5">Mot de passe (min. 8 caractères) *</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={f.password}
                  onChange={(e) => setF({ ...f, password: e.target.value })}
                  className="input-box pr-10"
                  autoComplete="new-password"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-2 top-2.5 text-[var(--muted)] hover:text-black"
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="label block mb-1.5">Confirmer mot de passe *</label>
              <div className="relative">
                <input
                  type={showPwdConfirm ? "text" : "password"}
                  value={f.password_confirm}
                  onChange={(e) => setF({ ...f, password_confirm: e.target.value })}
                  className={`${mismatchClass(f.password, f.password_confirm, pwdMatch)} pr-10`}
                  autoComplete="new-password"
                  onPaste={(e) => e.preventDefault()}
                  data-testid="signup-password-confirm-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwdConfirm(!showPwdConfirm)}
                  className="absolute right-2 top-2.5 text-[var(--muted)] hover:text-black"
                  aria-label={showPwdConfirm ? "Masquer la confirmation" : "Afficher la confirmation"}
                >
                  {showPwdConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {f.password_confirm && !pwdMatch && (
                <p className="font-mono text-[10px] text-red-600 mt-1" data-testid="password-mismatch">
                  Les deux mots de passe ne correspondent pas
                </p>
              )}
            </div>

            {error && (
              <div className="border-2 border-black p-3 text-xs bg-conflict-stripes-light font-mono" data-testid="signup-error">
                {error}
              </div>
            )}
            {/* Rôles */}
            <div>
              <label className="label block mb-1.5">Mon rôle *</label>
              <p className="text-xs text-[var(--muted)] mb-2 leading-relaxed">
                Choisis au moins un rôle. Tu peux choisir les deux si tu es à la fois arbitre et marqueur.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <label className={`p-3 border-2 cursor-pointer transition-colors flex items-start gap-2 ${f.role_referee ? "border-black bg-black/5" : "border-[var(--border-strong)]"}`} data-testid="role-referee-label">
                  <input
                    type="checkbox"
                    checked={f.role_referee}
                    onChange={(e) => setF({ ...f, role_referee: e.target.checked })}
                    className="mt-0.5 w-4 h-4 border-2 border-black"
                    data-testid="role-referee-input"
                  />
                  <span>
                    <div className="font-bold text-sm">Arbitre</div>
                    <div className="text-[11px] text-[var(--muted)]">Je peux arbitrer des parties.</div>
                  </span>
                </label>
                <label className={`p-3 border-2 cursor-pointer transition-colors flex items-start gap-2 ${f.role_scorer ? "border-black bg-black/5" : "border-[var(--border-strong)]"}`} data-testid="role-scorer-label">
                  <input
                    type="checkbox"
                    checked={f.role_scorer}
                    onChange={(e) => setF({ ...f, role_scorer: e.target.checked })}
                    className="mt-0.5 w-4 h-4 border-2 border-black"
                    data-testid="role-scorer-input"
                  />
                  <span>
                    <div className="font-bold text-sm">Marqueur</div>
                    <div className="text-[11px] text-[var(--muted)]">Je peux tenir la marque.</div>
                  </span>
                </label>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
              data-testid="signup-submit-btn"
            >
              <CheckSquare size={14} strokeWidth={2.5} />
              {loading ? "Création du compte…" : "Créer mon compte"}
            </button>
          </form>

          <p className="mt-6 pt-5 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            Déjà un compte? <a href="/officiel/login" className="text-black underline">Se connecter</a>
          </p>
        </div>
      </div>
    </div>
  );
}
